import React, { useState, useEffect } from 'react';
import { Menu, X, Phone, Globe } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import DivyadeepLogo from './DivyadeepLogo';

export default function Header() {
  const { language, toggleLanguage, t } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { key: 'nav_home', href: '#home' },
    { key: 'nav_services', href: '#services' },
    { key: 'nav_about', href: '#about' },
    { key: 'nav_results', href: '#results' },
    { key: 'nav_testimonials', href: '#testimonials' },
    { key: 'nav_contact', href: '#contact' },
  ];

  const scrollToSection = (href) => {
    setMobileOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <header
      data-testid="header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'backdrop-blur-xl bg-white/95 shadow-[0_4px_30px_rgba(0,0,0,0.06)] border-b border-[#D1A760]/10'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-12">
        <div className="flex items-center justify-between h-14 md:h-20">
          {/* Logo */}
          <a href="#home" data-testid="header-logo">
            <DivyadeepLogo size="md" variant={scrolled ? 'color' : 'light'} />
          </a>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-8">
            {navItems.map(item => (
              <button
                key={item.key}
                onClick={() => scrollToSection(item.href)}
                className={`text-sm font-manrope transition-colors duration-200 ${
                  scrolled 
                    ? 'text-[#475569] hover:text-[#0F172A]' 
                    : 'text-white/70 hover:text-white'
                }`}
                data-testid={`nav-${item.key}`}
              >
                {t(item.key)}
              </button>
            ))}
          </nav>

          {/* Right Actions */}
          <div className="flex items-center gap-3">
            {/* Language Toggle */}
            <button
              onClick={toggleLanguage}
              data-testid="language-toggle"
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-manrope transition-all duration-200 ${
                scrolled
                  ? 'border-slate-200 text-[#475569] hover:border-[#D1A760] hover:text-[#D1A760]'
                  : 'border-white/20 text-white/70 hover:border-white/40 hover:text-white'
              }`}
            >
              <Globe className="w-3.5 h-3.5" />
              {language === 'en' ? 'हिंदी' : 'EN'}
            </button>

            {/* Phone */}
            <a
              href="tel:+918303365990"
              className={`hidden md:flex items-center gap-1.5 text-sm transition-colors ${
                scrolled ? 'text-[#475569] hover:text-[#D1A760]' : 'text-white/70 hover:text-white'
              }`}
              data-testid="header-phone"
            >
              <Phone className="w-4 h-4" />
              <span className="text-xs font-manrope">083033 65990</span>
            </a>

            {/* Book CTA */}
            <button
              onClick={() => scrollToSection('#appointment')}
              data-testid="header-book-btn"
              className="hidden sm:block px-5 py-2.5 rounded-full bg-gradient-to-r from-[#D1A760] to-[#B89255] text-white text-sm font-manrope font-medium hover:shadow-[0_4px_15px_rgba(209,167,96,0.4)] transition-all duration-300"
            >
              {t('nav_book')}
            </button>

            {/* Mobile Menu */}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className={`lg:hidden p-2 ${scrolled ? 'text-[#0F172A]' : 'text-white'}`}
              data-testid="mobile-menu-toggle"
            >
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="lg:hidden bg-white/95 backdrop-blur-xl border-t border-slate-100 shadow-lg" data-testid="mobile-menu">
          <div className="px-6 py-4 space-y-1">
            {navItems.map(item => (
              <button
                key={item.key}
                onClick={() => scrollToSection(item.href)}
                className="block w-full text-left py-3 text-[#475569] font-manrope text-sm hover:text-[#0F172A] border-b border-slate-50"
              >
                {t(item.key)}
              </button>
            ))}
            <button
              onClick={() => scrollToSection('#appointment')}
              className="w-full mt-3 px-5 py-3 rounded-full bg-[#0F172A] text-white text-sm font-manrope font-medium"
            >
              {t('nav_book')}
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
